import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  LinearProgress,
  Chip,
  Tooltip,
  Button,
  Divider,
  Dialog, DialogContent, DialogTitle, IconButton,
} from "@mui/material";
import {
  FiCheckCircle,
  FiAlertCircle,
  FiLock,
  FiStar,
  FiLayers,
  FiArrowLeft,
  FiArrowRight,
  FiZap,
  FiGrid,
  FiX,
} from "react-icons/fi";

import CourseTimeline from "./CourseTimeline";
import { useSearchParams } from "react-router-dom";
import { auth } from "../firebase";

const COLORS = {
  bg: "#f5f0ff",
  white: "#ffffff",
  whiteSoft: "#fff8ff",
  whiteAlt: "#f7f2ff",
  subtle: "#e6dfef",
  red: "#ff3333",
  redDark: "#cc0000",
  textMain: "#2d233a",
  textMuted: "#6c6478",
  locked: "#d0cadb",
  line: "#d4ccdf",
};

const GRADIENTS = [
  "linear-gradient(135deg, #ff3333 0%, #ff6b6b 100%)",
  "linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)",
  "linear-gradient(135deg, #0ea5e9 0%, #22c55e 100%)",
  "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)",
  "linear-gradient(135deg, #06b6d4 0%, #8b5cf6 100%)",
  "linear-gradient(135deg, #fb7185 0%, #f97316 100%)",
];

const BACKEND_URL = "https://vol-backend.onrender.com";
const DEBUG = false;

// =======================
// 📋 TIPO_COLORS para avisos
// =======================
const TIPO_COLORS = {
  urgente: { color: "#e60000", bg: "#fff0f0", label: "IMPORTANTE" },
  evento: { color: "#7c3aed", bg: "#f3e8ff", label: "EVENTO" },
  recordatorio: { color: "#1565c0", bg: "#e3f2fd", label: "RECORDATORIO" },
  informativo: { color: "#2e7d32", bg: "#e8f5e9", label: "ANUNCIO" },
};

// =======================
// 📣 TABLÓN DE AVISOS
// =======================
function TablonAvisos({ programa = "TODOS" }) {
  const [avisos, setAvisos] = useState([]);
  const [todosAvisos, setTodosAvisos] = useState([]);
  const [selected, setSelected] = useState(null);
  const [verTodos, setVerTodos] = useState(false);
  const [loadingTodos, setLoadingTodos] = useState(false);

  useEffect(() => {
    const cargar = async () => {
      try {
        const token = await auth.currentUser?.getIdToken();
        const res = await fetch(`${BACKEND_URL}/avisos/publicos?limit=5&programa=${programa}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        setAvisos(data.avisos || []);
      } catch (e) {
        console.warn("Error cargando avisos:", e);
      }
    };
    cargar();
  }, [programa]);

  const handleVerTodos = async () => {
    setVerTodos(true);
    setLoadingTodos(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`${BACKEND_URL}/avisos/publicos?limit=50&programa=${programa}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setTodosAvisos(data.avisos || []);
    } catch (e) {
      console.warn("Error cargando todos los avisos:", e);
    } finally {
      setLoadingTodos(false);
    }
  };

  const t = selected ? (TIPO_COLORS[selected.tipo] || TIPO_COLORS.informativo) : null;

  const AvisoCard = ({ av, onClick }) => {
    const tc = TIPO_COLORS[av.tipo] || TIPO_COLORS.informativo;
    return (
      <Box onClick={() => onClick(av)} sx={{
        backgroundColor: "#fff", border: "1px solid #eee",
        borderRadius: 2, p: 1.5, display: "flex", gap: 1.2,
        alignItems: "flex-start", cursor: "pointer",
        "&:hover": { backgroundColor: "#f9f9f9", borderColor: tc.color },
      }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: 1.5, backgroundColor: tc.bg,
          flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: tc.color }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: "0.65rem", fontWeight: 800, color: tc.color, textTransform: "uppercase", mb: 0.2 }}>
            {tc.label}
          </Typography>
          <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#111", lineHeight: 1.2,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {av.titulo}
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", color: "#666", mt: 0.3, lineHeight: 1.3,
            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {av.mensaje}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: "0.68rem", color: "#aaa", flexShrink: 0 }}>
          {av.created_at ? new Date(av.created_at).toLocaleDateString("es-MX", { day: "2-digit", month: "short" }) : ""}
        </Typography>
      </Box>
    );
  };

  return (
    <Box sx={{ width: 260, flexShrink: 0, display: "flex", flexDirection: "column", gap: 1 }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
        <Typography sx={{ fontWeight: 800, fontSize: "0.85rem", color: "#111" }}>
          📣 TABLÓN DE AVISOS
        </Typography>
        <Typography onClick={handleVerTodos}
          sx={{ fontSize: "0.75rem", color: "#ff3333", cursor: "pointer", fontWeight: 600,
            "&:hover": { textDecoration: "underline" } }}>
          Ver todos
        </Typography>
      </Box>

      {/* Tarjetas preview (máx 5) */}
      {avisos.length === 0 ? (
        <Typography sx={{ fontSize: "0.8rem", color: "#aaa", textAlign: "center", py: 3 }}>
          Sin avisos activos.
        </Typography>
      ) : (
        avisos.map(av => <AvisoCard key={av.id} av={av} onClick={setSelected} />)
      )}

      {avisos.length > 0 && (
        <Box onClick={handleVerTodos} sx={{ border: "1px solid #eee", borderRadius: 2, p: 1.2,
          textAlign: "center", cursor: "pointer", "&:hover": { backgroundColor: "#f5f5f5" } }}>
          <Typography sx={{ fontSize: "0.8rem", color: "#333", fontWeight: 600 }}>
            Ver todos los avisos →
          </Typography>
        </Box>
      )}

      {/* ── Dialog VER TODOS ── */}
      <Dialog open={verTodos} onClose={() => setVerTodos(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3, maxHeight: "80vh" } }}>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center",
          borderBottom: "1px solid #eee", pb: 1.5 }}>
          <Typography sx={{ fontWeight: 800, fontSize: "1rem" }}>📣 Todos los avisos</Typography>
          <IconButton size="small" onClick={() => setVerTodos(false)}>
            <FiX size={16} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {loadingTodos ? (
            <Typography sx={{ textAlign: "center", color: "#aaa", py: 3 }}>Cargando avisos...</Typography>
          ) : todosAvisos.length === 0 ? (
            <Typography sx={{ textAlign: "center", color: "#aaa", py: 3 }}>Sin avisos activos.</Typography>
          ) : (
            <Stack spacing={1.2}>
              {todosAvisos.map(av => <AvisoCard key={av.id} av={av} onClick={(av) => { setVerTodos(false); setSelected(av); }} />)}
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Dialog DETALLE ── */}
      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        {selected && t && (
          <>
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center",
              borderBottom: `3px solid ${t.color}`, pb: 1.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: t.color }} />
                <Typography sx={{ fontWeight: 800, fontSize: "0.8rem", color: t.color, textTransform: "uppercase" }}>
                  {t.label}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => setSelected(null)}>
                <FiX size={16} />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 2.5 }}>
              <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", color: "#111", mb: 1 }}>
                {selected.titulo}
              </Typography>
              <Typography sx={{ fontSize: "0.88rem", color: "#444", lineHeight: 1.6, mb: 2 }}>
                {selected.mensaje}
              </Typography>
              {selected.fecha_evento && (
                <Typography sx={{ fontSize: "0.8rem", color: "#666", mb: 0.5 }}>
                  📅 <b>Fecha:</b> {new Date(selected.fecha_evento).toLocaleString("es-MX")}
                </Typography>
              )}
              {selected.ubicacion && (
                <Typography sx={{ fontSize: "0.8rem", color: "#666", mb: 0.5 }}>
                  📍 <b>Ubicación:</b> {selected.ubicacion}
                </Typography>
              )}
              {selected.fecha_limite && (
                <Typography sx={{ fontSize: "0.8rem", color: "#c62828", mb: 0.5 }}>
                  ⏰ <b>Vence:</b> {new Date(selected.fecha_limite).toLocaleDateString("es-MX")}
                </Typography>
              )}
              {selected.boton_label && selected.boton_url && (
                <Box component="a" href={selected.boton_url} target="_blank" rel="noopener noreferrer"
                  sx={{ display: "inline-block", mt: 2, px: 2.5, py: 1, borderRadius: 999,
                    backgroundColor: t.color, color: "#fff", fontSize: "0.85rem", fontWeight: 700,
                    textDecoration: "none", "&:hover": { opacity: 0.85 } }}>
                  {selected.boton_label} →
                </Box>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
}
// =======================
// 🧠 HELPER FUNCTIONS
// =======================
const safeTitle = (obj) => obj?.title || obj?.name || "Sin título";

const getBlockStats = (block) => {
  let total = 0;
  let done = 0;
  let xpTotal = 0;
  let xpEarned = 0;
  let modulesCount = (block?.modules || []).length;

  (block?.modules || []).forEach((m) => {
    (m?.activities || []).forEach((a) => {
      total += 1;
      const xp = a?.xp || 0;
      xpTotal += xp;
      if (a?.completed) {
        done += 1;
        xpEarned += xp;
      }
    });
  });

  const percent = total === 0 ? 0 : Math.round((done / total) * 100);
  const completed = total > 0 && done === total;

  return { total, done, percent, completed, xpTotal, xpEarned, modulesCount };
};

const getProgramStats = (blocks) => {
  let total = 0;
  let done = 0;
  let xpTotal = 0;
  let xpEarned = 0;

  (blocks || []).forEach((b) => {
    const s = getBlockStats(b);
    total += s.total;
    done += s.done;
    xpTotal += s.xpTotal;
    xpEarned += s.xpEarned;
  });

  const percent = total === 0 ? 0 : Math.round((done / total) * 100);
  return { total, done, percent, xpTotal, xpEarned };
};

// usa status académico del bloque (4 reglas en cascada)
const isBlockLocked = (blocks, index, blockStatuses = {}) => {
  if (index === 0) return false;

  const block    = blocks[index];
  const bid      = block?.id;
  const prevBlock = blocks[index - 1];
  const prevId   = prevBlock?.id;

  // Regla 1: el bloque ACTUAL ya tiene actividad → nunca bloqueado
  if (bid && blockStatuses[bid]?.status) {
    const s = blockStatuses[bid].status;
    if (s === 'en_progreso' || s === 'aprobado' || s === 'completado') return false;
  }

  // Regla 2: el backend dice explícitamente que es accesible
  if (bid && blockStatuses[bid]?.accesible === true) return false;

  // Regla 3: el bloque ANTERIOR está aprobado o completado → desbloquear este
  // (cubre el caso en que el cron aprobó el bloque previo pero aún no desbloqueó este)
  if (prevId && blockStatuses[prevId]?.status) {
    const s = blockStatuses[prevId].status;
    if (s === 'aprobado' || s === 'completado') return false;
  }

  // Regla 4 (fallback): el bloque anterior tiene 100 % de actividades completadas
  const prevStats = getBlockStats(prevBlock);
  if (prevStats.completed) return false;

  return true;
};

const getBlockGradient = (index) => GRADIENTS[index % GRADIENTS.length];

const getStatusMeta = ({ locked, completed }) => {
  if (locked) return { label: "BLOQUEADO", icon: <FiLock size={14} /> };
  if (completed) return { label: "COMPLETADO", icon: <FiCheckCircle size={14} /> };
  return { label: "EN PROGRESO", icon: <FiAlertCircle size={14} /> };
};

const normalizeFromBackend = ({ catalog, progress }) => {
  const program = catalog?.program || null;

  const progressMap = {};
  (progress?.activities || []).forEach((a) => {
    if (a?.activity_id) progressMap[a.activity_id] = a;
  });

  const blocks = (catalog?.blocks || []).map((b) => {
    const blockId = b?.id ?? b?.block_id ?? null;

    const modules = (b?.modules || []).map((m) => {
      const moduleId = m?.id ?? m?.module_id ?? null;

      const activities = (m?.activities || []).map((a) => {
        const activityId = a?.id ?? a?.activity_id ?? null;
        const p = activityId ? progressMap[activityId] : null;

        return {
          ...a,
          id: activityId,
          order: a?.order ?? a?.order_index ?? a?.orderIndex ?? 999,
          title: a?.title ?? a?.name ?? "Actividad",
          completed: p?.status === "completed" || p?.status === "graded" || a?.completed === true,
          score: p?.score ?? a?.score ?? null,
          status: p?.status ?? a?.status ?? "not_started",
        };
      });

      return {
        ...m,
        id: moduleId,
        order: m?.order ?? m?.order_index ?? m?.orderIndex ?? 999,
        title: m?.title ?? m?.name ?? "Módulo",
        activities,
      };
    });

    return {
      ...b,
      id: blockId,
      order: b?.order ?? b?.order_index ?? b?.orderIndex ?? 999,
      title: b?.title ?? b?.name ?? "Bloque",
      modules,
    };
  });

  const sortedBlocks = [...blocks].sort((a, b) => (a?.order ?? 999) - (b?.order ?? 999));
  sortedBlocks.forEach((blk) => {
    blk.modules = [...(blk.modules || [])].sort((a, b) => (a?.order ?? 999) - (b?.order ?? 999));
    (blk.modules || []).forEach((m) => {
      m.activities = [...(m.activities || [])].sort((a, b) => (a?.order ?? 999) - (b?.order ?? 999));
    });
  });

  return { program, blocks: sortedBlocks };
};

// =======================
// 🧩 COMPONENTE PRINCIPAL
// =======================
const BlockTimeline = ({
  programCode: programCodeProp = null,
  programId = null,
  programs = [],
  initialBlockId = null,
  onActivityClick,
}) => {
  const [searchParams] = useSearchParams();

  const programCode = String(
    programCodeProp || searchParams.get("code") || programId || ""
  )
    .trim()
    .toUpperCase();

  const [program, setProgram] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  
  // ✅ NUEVO: Estado para guardar los statuses de cada bloque
  const [blockStatuses, setBlockStatuses] = useState({});

  const programStats = useMemo(() => getProgramStats(blocks), [blocks]);

  const [selectedBlockId, setSelectedBlockId] = useState(initialBlockId || null);
  const [view, setView] = useState("blocks");

  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true);
        setLoadError(null);

        if (!programCode) throw new Error("Falta programCode (ej: SV).");

        const user = auth.currentUser;
        if (!user) throw new Error("Usuario no autenticado");

        const token = await user.getIdToken(true);

        const catalogUrl = `${BACKEND_URL}/progreso/catalogo/programas/${encodeURIComponent(
          programCode
        )}/arbol`;

        const progressUrl = `${BACKEND_URL}/progreso/me/programas/${encodeURIComponent(
          programCode
        )}`;

        if (DEBUG) {
          console.log("📡 catalogUrl:", catalogUrl);
          console.log("📡 progressUrl:", progressUrl);
        }

        const [catalogRes, progressRes] = await Promise.all([
          fetch(catalogUrl, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(progressUrl, { headers: { Authorization: `Bearer ${token}` } }),
        ]);

        if (!catalogRes.ok) {
          let msg = `No se pudo cargar catálogo (${catalogRes.status})`;
          try {
            const ej = await catalogRes.json();
            msg = ej?.error || ej?.message || msg;
          } catch {}
          throw new Error(msg);
        }

        if (!progressRes.ok) {
          let msg = `No se pudo cargar progreso (${progressRes.status})`;
          try {
            const ej = await progressRes.json();
            msg = ej?.error || ej?.message || msg;
          } catch {}
          throw new Error(msg);
        }

        const catalog = await catalogRes.json();
        const progress = await progressRes.json();

        const normalized = normalizeFromBackend({ catalog, progress });

        setProgram(normalized.program || { title: programCode, description: "" });
        setBlocks(normalized.blocks || []);

        const firstId = normalized.blocks?.[0]?.id || null;
        const initial = initialBlockId || firstId;

        setSelectedBlockId(initial);
        setView("blocks");
      } catch (e) {
        console.error("❌ BlockTimeline error:", e);
        setLoadError(e?.message || "Error cargando programa");
        setProgram(null);
        setBlocks([]);
      } finally {
        setLoading(false);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programCode]);

  // ✅ NUEVO: useEffect para cargar el estado académico de cada bloque
  useEffect(() => {
  const cargarEstadosBloques = async () => {
    if (!blocks.length || !programCode) return;
    const user = auth.currentUser;
    if (!user) return;
    try {
      const token = await user.getIdToken(true);
      const res = await fetch(
        `${BACKEND_URL}/progreso/me/programas/${encodeURIComponent(programCode)}/estados-bloques`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) return;
      const data = await res.json();
      setBlockStatuses(data.bloques || {});
    } catch (e) {
      console.warn('[BlockTimeline] Error cargando estados de bloques:', e);
    }
  };
  cargarEstadosBloques();
}, [blocks, programCode]);

  useEffect(() => {
    if (!blocks?.length) return;
    if (!selectedBlockId) {
      setSelectedBlockId(blocks?.[0]?.id || null);
      return;
    }
    const exists = blocks.some((b) => b.id === selectedBlockId);
    if (!exists) setSelectedBlockId(blocks?.[0]?.id || null);
  }, [blocks, selectedBlockId]);

  const selectedBlock = useMemo(() => {
    return blocks.find((b) => b.id === selectedBlockId) || null;
  }, [blocks, selectedBlockId]);

  const handleProgressUpdate = ({ blockId, moduleId, activityId, patch }) => {
    setBlocks((prev) =>
      prev.map((b) => {
        if (b.id !== blockId) return b;
        return {
          ...b,
          modules: (b.modules || []).map((m) => {
            if (m.id !== moduleId) return m;
            return {
              ...m,
              activities: (m.activities || []).map((a) => {
                if (a.id !== activityId) return a;
                return { ...a, ...patch };
              }),
            };
          }),
        };
      })
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography>Cargando programa…</Typography>
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{loadError}</Typography>
      </Box>
    );
  }

  if (!program) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">No encontré el programa.</Typography>
      </Box>
    );
  }

  const goToCourse = (blockId) => {
    setSelectedBlockId(blockId);
    setView("course");
  };
  
  // =======================
  // 🎨 RENDER PRINCIPAL
  // =======================
  return (
    <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start", width: "100%" }}>
      {/* CONTENIDO PRINCIPAL */}
      <Box
        sx={{
          flex: 1,
          maxWidth: 1250,
          mx: "auto",
          py: 3,
          px: { xs: 1.5, md: 2 },
          backgroundColor: COLORS.bg,
        }}
      >
        {/* HEADER PROGRAMA */}
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: 2.5,
            borderRadius: 3,
            backgroundColor: COLORS.whiteSoft,
            border: `1px solid ${COLORS.subtle}`,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              opacity: 1,
              pointerEvents: "none",
              border: "6px solid red",
              borderRadius: 2,
            }}
          />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            sx={{ position: "relative", borderRadius: 2 }}
          >
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: 2,
                    background:
                      "linear-gradient(135deg, #cc0000 0%, #ff3333 65%, #ff3939ff 100%)",
                    display: "grid",
                    placeItems: "center",
                    boxShadow: "0 10px 18px rgba(0,0,0,0.10)",
                  }}
                >
                  <FiGrid size={16} color="#fff" />
                </Box>

                <Typography variant="h6" sx={{ fontWeight: 900, color: COLORS.textMain }}>
                  {safeTitle(program)}
                </Typography>
              </Stack>

              <Typography variant="body2" sx={{ color: COLORS.textMuted, mt: 0.5 }}>
                {program?.description || "Selecciona un bloque para ver sus módulos."}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="flex-end">
              {programStats.xpTotal > 0 && (
                <Chip
                  icon={<FiStar size={16} />}
                  label={`${programStats.xpEarned}/${programStats.xpTotal} XP`}
                  sx={{
                    backgroundColor: COLORS.white,
                    border: `1px solid ${COLORS.subtle}`,
                    fontWeight: 800,
                  }}
                />
              )}
              <Chip
                icon={<FiZap size={16} />}
                label={`${programStats.percent}%`}
                sx={{
                  backgroundColor: COLORS.white,
                  border: `1px solid ${COLORS.subtle}`,
                  fontWeight: 800,
                }}
              />
            </Stack>
          </Stack>

          <Box mt={2} sx={{ position: "relative" }}>
            <LinearProgress
              variant="determinate"
              value={programStats.percent}
              sx={{
                height: 10,
                borderRadius: 999,
                backgroundColor: COLORS.subtle,
                "& .MuiLinearProgress-bar": {
                  borderRadius: 999,
                  backgroundColor: COLORS.red,
                },
              }}
            />
            <Typography
              variant="caption"
              sx={{
                display: "block",
                textAlign: "right",
                mt: 0.6,
                color: COLORS.textMuted,
                fontWeight: 800,
              }}
            >
              {programStats.done}/{programStats.total} actividades
            </Typography>
          </Box>
        </Paper>

        {/* VIEW: COURSE */}
        {view === "course" && (
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              borderRadius: 3,
              backgroundColor: COLORS.whiteAlt,
              border: `1px solid ${COLORS.subtle}`,
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              sx={{ px: 0.5, pt: 0.5, pb: 1 }}
            >
              <Button
                onClick={() => setView("blocks")}
                startIcon={<FiArrowLeft size={16} />}
                sx={{
                  textTransform: "none",
                  fontWeight: 900,
                  color: COLORS.textMain,
                  borderRadius: 999,
                }}
              >
                Volver a bloques
              </Button>

              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 900,
                  color: COLORS.textMain,
                  textAlign: { xs: "left", sm: "right" },
                }}
              >
                {selectedBlock ? safeTitle(selectedBlock) : "Selecciona un bloque"}
              </Typography>
            </Stack>

            <CourseTimeline
              modules={(selectedBlock?.modules || [])
                .slice()
                .sort((a, b) => (a?.order ?? 999) - (b?.order ?? 999))}
              context={{
                programId: programCode,
                programTitle: safeTitle(program),
                blockId: selectedBlock?.id,
                blockTitle: safeTitle(selectedBlock),
              }}
              onActivityClick={onActivityClick}
              onProgressUpdate={handleProgressUpdate}
              hideOuterContainer
            />
          </Paper>
        )}

        {/* VIEW: BLOCKS */}
        {view === "blocks" && (
          <Box sx={{ width: "100%" }}>
            <Box sx={{ position: "relative", py: 1 }}>
              <Box
                sx={{
                  position: "absolute",
                  left: "50%",
                  top: 0,
                  bottom: 0,
                  width: 2,
                  transform: "translateX(-50%)",
                  background: `linear-gradient(180deg, ${COLORS.line}, rgba(212,204,223,0.0))`,
                  display: { xs: "none", md: "block" },
                }}
              />

              <Stack spacing={2.5}>
                {blocks.map((block, index) => {
                  const stats = getBlockStats(block);
                  const locked = isBlockLocked(blocks, index, blockStatuses);
                  const gradient = getBlockGradient(index);
                  const status = getStatusMeta({ locked, completed: stats.completed });

                  const isLeft = index % 2 === 0;
                  const sideJustify = { xs: "flex-start", md: isLeft ? "flex-start" : "flex-end" };
                  const cardWidth = { xs: "100%", md: "48%" };

                  return (
                    <Box
                      key={block.id}
                      sx={{
                        display: "flex",
                        justifyContent: sideJustify,
                        position: "relative",
                      }}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          left: "50%",
                          top: 28,
                          transform: "translateX(-50%)",
                          width: 18,
                          height: 18,
                          borderRadius: "50%",
                          background: locked
                            ? COLORS.locked
                            : stats.completed
                            ? "#e0f8ec"
                            : "#fff6e6",
                          border: `2px solid ${
                            locked ? "#a9a2b6" : stats.completed ? "#1c7d4f" : "#ff9800"
                          }`,
                          boxShadow: "0 10px 18px rgba(0,0,0,0.10)",
                          display: { xs: "none", md: "block" },
                          zIndex: 2,
                        }}
                      />

                      <Paper
                        elevation={0}
                        onClick={() => !locked && goToCourse(block.id)}
                        sx={{
                          width: cardWidth,
                          borderRadius: 4,
                          overflow: "hidden",
                          cursor: locked ? "not-allowed" : "pointer",
                          border: `1px solid ${COLORS.subtle}`,
                          backgroundColor: "rgba(255,255,255,0.72)",
                          backdropFilter: "blur(10px)",
                          position: "relative",
                          opacity: locked ? 0.72 : 1,
                          transform: "translateY(0px)",
                          transition: "transform 160ms ease, box-shadow 160ms ease, opacity 160ms ease",
                          boxShadow: "0 12px 24px rgba(0,0,0,0.10)",
                          "&:hover": locked
                            ? {}
                            : {
                                transform: "translateY(-2px)",
                                boxShadow: "0 16px 34px rgba(0,0,0,0.14)",
                              },
                        }}
                      >
                        <Box
                          sx={{
                            height: 64,
                            background: locked ? "linear-gradient(135deg, #bdb6c9, #d8d2e3)" : gradient,
                            position: "relative",
                          }}
                        >
                          <Box
                            sx={{
                              position: "absolute",
                              inset: 0,
                              background:
                                "radial-gradient(circle at 20% 30%, rgba(255,255,255,0.25), transparent 35%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.18), transparent 40%)",
                              pointerEvents: "none",
                            }}
                          />

                          <Box
                            sx={{
                              position: "absolute",
                              left: 16,
                              top: 14,
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              px: 1.2,
                              py: 0.6,
                              borderRadius: 999,
                              backgroundColor: "rgba(255,255,255,0.20)",
                              border: "1px solid rgba(255,255,255,0.35)",
                              color: "#fff",
                              fontWeight: 900,
                              letterSpacing: 1,
                              boxShadow: "0 10px 18px rgba(0,0,0,0.10)",
                            }}
                          >
                            <FiLayers size={14} />
                            <span>BLOQUE {block?.order ?? index + 1}</span>
                          </Box>

                          <Box
                            sx={{
                              position: "absolute",
                              right: 16,
                              top: 14,
                              display: "flex",
                              alignItems: "center",
                              gap: 0.8,
                              px: 1.1,
                              py: 0.6,
                              borderRadius: 999,
                              backgroundColor: "rgba(0,0,0,0.18)",
                              border: "1px solid rgba(255,255,255,0.22)",
                              color: "#fff",
                              fontWeight: 800,
                              letterSpacing: 0.6,
                            }}
                          >
                            {status.icon}
                            <span style={{ fontSize: 12 }}>{status.label}</span>
                          </Box>
                        </Box>

                        <Box sx={{ p: 2.2 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 950,
                              color: COLORS.textMain,
                              lineHeight: 1.1,
                              mb: 0.5,
                            }}
                          >
                            {safeTitle(block)}
                          </Typography>

                          <Typography variant="body2" sx={{ color: COLORS.textMuted, mb: 1.6 }}>
                            {block?.description || "Avanza para desbloquear el siguiente bloque."}
                          </Typography>

                          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" sx={{ mb: 1.2 }}>
                            <Chip
                              size="small"
                              icon={<FiZap size={14} />}
                              label={`${stats.percent}%`}
                              sx={{
                                fontWeight: 900,
                                backgroundColor: COLORS.white,
                                border: `1px solid ${COLORS.subtle}`,
                              }}
                            />
                            {stats.xpTotal > 0 && (
                              <Chip
                                size="small"
                                icon={<FiStar size={14} />}
                                label={`${stats.xpEarned}/${stats.xpTotal} XP`}
                                sx={{
                                  fontWeight: 900,
                                  backgroundColor: COLORS.white,
                                  border: `1px solid ${COLORS.subtle}`,
                                }}
                              />
                            )}
                            <Chip
                              size="small"
                              icon={<FiLayers size={14} />}
                              label={`${stats.modulesCount} módulos`}
                              sx={{
                                fontWeight: 900,
                                backgroundColor: COLORS.white,
                                border: `1px solid ${COLORS.subtle}`,
                              }}
                            />
                          </Stack>

                          <Box sx={{ mb: 1.2 }}>
                            <LinearProgress
                              variant="determinate"
                              value={stats.percent}
                              sx={{
                                height: 9,
                                borderRadius: 999,
                                backgroundColor: COLORS.subtle,
                                "& .MuiLinearProgress-bar": {
                                  borderRadius: 999,
                                  backgroundColor: locked ? "#8f889d" : COLORS.red,
                                },
                              }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                mt: 0.6,
                                color: COLORS.textMuted,
                                fontWeight: 800,
                              }}
                            >
                              {stats.done}/{stats.total} actividades
                            </Typography>
                          </Box>

                          <Divider sx={{ borderColor: COLORS.subtle, mb: 1.2 }} />

                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="caption" sx={{ color: COLORS.textMuted, fontWeight: 800 }}>
                              {locked
                                ? "Completa el bloque anterior para entrar."
                                : stats.completed
                                ? "Listo. Puedes repasar cuando quieras."
                                : "Siguiente paso: entra y avanza."}
                            </Typography>

                            <Tooltip title={locked ? "Bloqueado" : "Abrir bloque"}>
                              <Box
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 999,
                                  display: "grid",
                                  placeItems: "center",
                                  background: locked ? "#d7d0e3" : gradient,
                                  color: locked ? "#4b4456" : "#fff",
                                  border: `1px solid ${COLORS.subtle}`,
                                  boxShadow: "0 10px 18px rgba(0,0,0,0.10)",
                                }}
                              >
                                {locked ? (
                                  <FiLock size={18} />
                                ) : isLeft ? (
                                  <FiArrowRight size={18} />
                                ) : (
                                  <FiArrowLeft size={18} />
                                )}
                              </Box>
                            </Tooltip>
                          </Stack>
                        </Box>
                      </Paper>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          </Box>
        )}
      </Box>

      {/* 📣 TABLÓN DE AVISOS — LADO DERECHO */}
      <Box sx={{ pt: 3, display: { xs: "none", lg: "block" } }}>
        <TablonAvisos programa={programCode} />
      </Box>
    </Box>
  );
};

export default BlockTimeline;